import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import CalibrationApi from "../../../../../api/CalibrationApi";
import {
  CartesianCoordinateHotelConfig,
  Ri360AutoCalibrationFineTuningResult,
  Ri360AutoCalibrationState,
} from "../../../../../Model/Calibration";
import { ECarrier } from "../../../../../Model/ECarrier";
import { EModuleType } from "../../../../../Model/EModuleType";
import { ENotificationType } from "../../../../../Model/ENotificationType";
import {
  EPsrState,
  ESuiteModeState,
  ESuiteMode,
} from "../../../../../Model/ModuleState";
import { Ri360CarrierCalibration } from "../../../../../Model/StorageConfig";
import { sendNotification } from "../../../../../redux/actions/NotificationAction";
import { getModuleState } from "../../../../../redux/reducers/ModuleStateReducer";
import { useTypedSelector } from "../../../../../redux/reducers/RootReducer";
import AutoCalibrationUtil from "../../../../../Utility/AutoCalibrationUtil";
import MtiButton from "../../../../Buttons/MtiButton";
import Collapse from "../../../../Common/Collapse";
import CommonDropDown from "../../../../Common/DropDown/CommonDropDown";
import Disabled from "../../../../Controls/Disabled";
import AccordionToggle from "../../../../Plate/AccordionToggle";
import CalibrationFailModal from "../AutoCalibration/CalibrationFailModal";
import CalibrationProgressModal from "../AutoCalibration/CalibrationProgressModal";
import CalibrationSuccessModal from "../AutoCalibration/CalibrationSuccessModal";
import RunSingleAutoCalibrationModal from "../AutoCalibration/RunSingleAutoCalibrationModal";
import UseManualCalibrationModal from "../AutoCalibration/UseManualCalibrationModal";
import AutoCalibrationResultWithFineTuning from "./AutoCalibrationResultWithFineTuning";
import AutoCalibrationTest from "./AutoCalibrationTest";
import "./AutoCalibrationCommandPanel.css";
import { CartesianCoordinate } from "../../../../../Model/Coordinate";

const storageView = require("../vector-deviation.png");

type CarrierDisplayValue = {
  display: string;
  value: ECarrier;
  disabled: boolean;
};

type PopupModalState = "IDLE" | "INPROGRESS" | "SUCCESS" | "FAIL";

const getInitialModalState = (
  suiteMode: ESuiteMode,
  psrState: EPsrState,
  autoCalibratingState: ESuiteModeState
): PopupModalState => {
  if (
    suiteMode === ESuiteMode.AutoCalibrating &&
    psrState === EPsrState.Busy &&
    (autoCalibratingState === ESuiteModeState.Started ||
      autoCalibratingState === ESuiteModeState.Stopping)
  ) {
    return "INPROGRESS";
  } else {
    return "IDLE";
  }
};

const getUserIntentionallyStopLocalStorage = (
  autoCalibratingState: ESuiteModeState
) => {
  if (autoCalibratingState === ESuiteModeState.Stopping) {
    const resultString = localStorage.getItem("USER_STOP_AUTO_CALIBRATION");
    return resultString === "true" ? true : false;
  }

  return false;
};

const setUserIntentionallyStopLocalStorage = (value: boolean) => {
  localStorage.setItem("USER_STOP_AUTO_CALIBRATION", value.toString());
};

const CALIBRATION_RESULT = 0;
const CALIBRATION_TEST = 1;
export const AUTO_CAL_ACCORDION_STATE = "AUTO_CAL_ACCORDION_STATE";

function getAccordionState(key: string): boolean[] {
  const value = localStorage.getItem(key);
  if (value != null) {
    return JSON.parse(value);
  } else {
    return [false, false];
  }
}

function getExpandAllAccordionState(key: string): boolean {
  const value = localStorage.getItem(key);
  if (value != null) {
    const accordionState: boolean[] = JSON.parse(value);
    return accordionState.some((val) => val === false);
  } else {
    return true;
  }
}

function getCarrierAutoCalibrationFromCarrierFineTuning(
  carrierFineTuningResult: Ri360AutoCalibrationFineTuningResult[]
): CartesianCoordinateHotelConfig[] {
  return carrierFineTuningResult.map((c) => {
    return {
      hotelNumber: c.hotelNumber,
      top: {
        x: c.topUpdated.x + c.topOffset.x,
        y: c.topUpdated.y + c.topOffset.y,
        z: c.topUpdated.z + c.topOffset.z,
      },
      bottom: {
        x: c.bottomUpdated.x + c.bottomOffset.x,
        y: c.bottomUpdated.y + c.bottomOffset.y,
        z: c.bottomUpdated.z + c.bottomOffset.z,
      },
    };
  });
}

type Props = {
  setOnApply: (onApply: { execute: () => void; disabled: boolean }) => void;
  setOnCancel: (onCancel: { execute: () => void; disabled: boolean }) => void;
  isCalibrationUpdated: boolean;
  setIsCalibrationUpdated: (isCalibrationUpdated: boolean) => void;
  psrIdentifier: string;
  triggerExpandAll?: boolean; // use boolean state, true = trigger expand/collaps all
  setTriggerExpandAll?: (triiger: boolean) => void; // update the trigger boolean state
  onAllItemCollapsed?: (collapsed: boolean) => void; // called when all accordion collapsed or not
};

export default function AutoCalibrationCommandPanel({
  setOnApply,
  setOnCancel,
  isCalibrationUpdated,
  setIsCalibrationUpdated,
  psrIdentifier,
  triggerExpandAll,
  setTriggerExpandAll = () => {},
  onAllItemCollapsed = () => {},
}: Props) {
  const history = useHistory();
  const dispatch = useDispatch();

  //-------Redux Selector-------//
  const suiteState = useTypedSelector(
    (state) => state.moduleStateStore.moduleStateById["Suite"]?.suite
  );

  const psrState = useTypedSelector(
    (state) => getModuleState(state, psrIdentifier).psr
  );

  if (psrState == null) {
    throw new Error("Psr state is null");
  }

  //-------END Redux Selector-------//

  //-------Variable-------//
  const suiteMode = suiteState?.suiteMode ?? ESuiteMode.AutoCalibrating;
  const autoCalibratingState = suiteState?.autoCalibrating;
  //-------End Variable-------//

  //-------State-------//
  const [showUseManualCalModal, setShowUseManualCalModal] = useState(false);
  const [showRunAutoCalibrationModal, setShowRunAutoCalibrationModal] =
    useState(false);

  const [vectorDeviation, setVectorDeviation] = useState<CartesianCoordinate>({
    x: 0,
    y: 0,
    z: 0,
  });
  const [vectorDeviations, setVectorDeviations] = useState<{
    [index: string]: CartesianCoordinate;
  }>({});

  const [autoCalibrationResults, setAutoCalibrationResults] = useState<
    Ri360AutoCalibrationState[]
  >([]);

  const [carrierAutoCalibrationResult, setCarrierAutoCalibrationResult] =
    useState<Ri360AutoCalibrationState | null>(null);

  const [carrierFineTuningResult, setCarrierFineTuningResult] = useState<
    Ri360AutoCalibrationFineTuningResult[]
  >([]);

  const [selectedCarrier, setSelectedCarrier] = useState(
    ECarrier.FixedCarriage
  );

  const [carrierDisplayValues, setCarrierDisplayValues] = useState<
    CarrierDisplayValue[]
  >([
    {
      display: AutoCalibrationUtil.getCarrierName(ECarrier.FixedCarriage),
      disabled: false,
      value: ECarrier.FixedCarriage,
    },
  ]);

  const [storageCalibrations, setStorageCalibrations] = useState(
    [] as Ri360CarrierCalibration[]
  );

  const [carrierStorageCalibration, setCarrierStorageCalibration] =
    useState<Ri360CarrierCalibration | null>(null);

  const [canExpandAll, setCanExpandAll] = useState(
    getExpandAllAccordionState(AUTO_CAL_ACCORDION_STATE)
  );
  const [accordionState, setAccordionState] = useState(
    getAccordionState(AUTO_CAL_ACCORDION_STATE)
  );

  const [popupModalState, setPopupModalState] = useState(
    getInitialModalState(
      suiteMode,
      psrState?.state ?? EPsrState.Unknown,
      autoCalibratingState?.modeState ?? ESuiteModeState.Starting
    )
  );
  const [disableDoneAnimation, setDisableDoneAnimation] = useState(false);

  // need this because user cancel / stop autoCalibration will not show success / failed modal
  // and system will change psrState -> fail when autoCalibration is cancel / stoped
  const [userIntentionallyStop, setUserIntentionallyStop] = useState(
    getUserIntentionallyStopLocalStorage(
      autoCalibratingState?.modeState ?? ESuiteModeState.Starting
    )
  );

  const [showStorageView, setShowStorageView] = useState(false);

  //-------End State-------//

  //-------Effect-------//

  useEffect(() => {
    fetchAutoCalibration();
    fetchVectorDeviation();
  }, []);

  useEffect(() => {
    CalibrationApi.getStorageCalibration().then((storageCalibration) => {
      setStorageCalibrations(storageCalibration.ri360StorageCalibrations);
      const carrierStorageCalibration =
        storageCalibration.ri360StorageCalibrations.filter(
          (c) => c.carrier === selectedCarrier
        )[0];
      setCarrierStorageCalibration(carrierStorageCalibration);
    });
  }, []);

  const needShowModalAfterProgress = useRef(popupModalState === "INPROGRESS");

  // popup state changes logic
  useEffect(() => {
    if (suiteMode === ESuiteMode.AutoCalibrating) {
      //show in progress only when psr is busy
      if ((psrState ? psrState.state : EPsrState.Unknown) === EPsrState.Busy) {
        setPopupModalState("INPROGRESS");
        needShowModalAfterProgress.current = true;
        setShowRunAutoCalibrationModal(false);
        setDisableDoneAnimation(false);
      }
    } else if (suiteMode === ESuiteMode.ArmCrashed) {
      setPopupModalState("FAIL");
      needShowModalAfterProgress.current = false;
      setDisableDoneAnimation(true);
    } else if (needShowModalAfterProgress.current) {
      if (psrState?.state === EPsrState.Fail) {
        if (!userIntentionallyStop) {
          setPopupModalState("FAIL");
        } else {
          setPopupModalState("IDLE");
          needShowModalAfterProgress.current = false;
        }
      } else {
        setIsCalibrationUpdated(true);
        fetchAutoCalibration();
        setPopupModalState("SUCCESS");
      }
    }
  }, [psrState?.state, suiteMode, autoCalibratingState]);

  useEffect(() => {
    setOnApply({
      execute: onApply,
      disabled: !isCalibrationUpdated,
    });
    setOnCancel({
      execute: onCancel,
      disabled: !isCalibrationUpdated,
    });
  }, [isCalibrationUpdated, carrierFineTuningResult, selectedCarrier]);

  useEffect(() => {
    localStorage.setItem(
      AUTO_CAL_ACCORDION_STATE,
      JSON.stringify(accordionState)
    );
  }, [accordionState]);

  useEffect(() => {
    if (triggerExpandAll) {
      onExpandAllToggleClicked();
      setTriggerExpandAll(false);
    }
  }, [triggerExpandAll]);

  useEffect(() => {
    onAllItemCollapsed(canExpandAll);
  }, [canExpandAll]);

  //-------Function-------//

  const updateFineTuningResult = (
    carrierAutoCalibrationResult: Ri360AutoCalibrationState
  ) => {
    let fineTuningResults: Ri360AutoCalibrationFineTuningResult[] = [];

    for (
      let i = 0;
      i < carrierAutoCalibrationResult.currentPositions.length;
      i++
    ) {
      fineTuningResults.push({
        hotelNumber:
          carrierAutoCalibrationResult.currentPositions[i].hotelNumber,
        topUpdated: carrierAutoCalibrationResult.updatedPositions
          ? carrierAutoCalibrationResult.updatedPositions[i].top
          : carrierAutoCalibrationResult.currentPositions[i].top
          ? carrierAutoCalibrationResult.currentPositions[i].top
          : { x: 0, y: 0, z: 0 },
        bottomUpdated: carrierAutoCalibrationResult.updatedPositions
          ? carrierAutoCalibrationResult.updatedPositions[i].bottom
          : carrierAutoCalibrationResult.currentPositions[i].bottom
          ? carrierAutoCalibrationResult.currentPositions[i].bottom
          : { x: 0, y: 0, z: 0 },
        topOffset: { x: 0, y: 0, z: 0 },
        bottomOffset: { x: 0, y: 0, z: 0 },
        topCurrent: carrierAutoCalibrationResult.currentPositions[i].top ?? {
          x: 0,
          y: 0,
          z: 0,
        },
        bottomCurrent: carrierAutoCalibrationResult.currentPositions[i]
          .bottom ?? { x: 0, y: 0, z: 0 },
      });
    }

    setCarrierFineTuningResult(fineTuningResults);
  };

  const fetchAutoCalibration = () => {
    CalibrationApi.GetAutoCalibrationResult().then((autoCalibrationResults) => {
      setAutoCalibrationResults(autoCalibrationResults);
      const carrierDisplayValues: CarrierDisplayValue[] =
        autoCalibrationResults.map((c) => {
          return {
            display: AutoCalibrationUtil.getCarrierName(c.carrier),
            disabled: false,
            value: c.carrier,
          };
        });
      setCarrierDisplayValues(carrierDisplayValues);
      updateFineTuningResult(autoCalibrationResults[0]);
      setCarrierAutoCalibrationResult(autoCalibrationResults[0]);
    });
  };

  const fetchVectorDeviation = () => {
    CalibrationApi.GetVectorDeviation().then((vectorDeviations) => {
      setVectorDeviations(vectorDeviations);
      console.log("DEBUG", ECarrier[selectedCarrier]);
      setVectorDeviation(vectorDeviations[ECarrier[selectedCarrier]]);
    });
  };

  const onApply = () => {
    CalibrationApi.updateStorageAutoCalibration(
      selectedCarrier,
      getCarrierAutoCalibrationFromCarrierFineTuning(carrierFineTuningResult)
    ).then(() => {
      CalibrationApi.saveStorageAutoCalibration("SUITE", EModuleType.PSR).then(
        (result) => {
          setIsCalibrationUpdated(false);
          fetchAutoCalibration();
          fetchVectorDeviation();
          dispatch(
            sendNotification(
              ENotificationType.Success,
              "The changes has been saved successfully."
            )
          );
        }
      );
    });
  };

  const onCancel = () => {
    CalibrationApi.discardStorageAutoCalibration("SUITE", EModuleType.PSR);
    setIsCalibrationUpdated(false);
    fetchAutoCalibration();
  };

  const stopAutoCalibration = () => {
    setUserIntentionallyStop(true);
    setUserIntentionallyStopLocalStorage(true);
    CalibrationApi.stopStorageAutoCalibration("SUITE", EModuleType.PSR);
  };

  const useManualCalibration = () => {
    CalibrationApi.useManualCalibration("SUITE", EModuleType.PSR, [
      selectedCarrier,
    ]).then((data) => {
      history.push("/settings/vector-editor");
    });
  };

  const onHideDone = useCallback(() => {
    setPopupModalState("IDLE");
    needShowModalAfterProgress.current = false;
  }, []);

  const onAccordionToggleClicked = (id: number) => {
    const isOpening = !accordionState[id];
    let state;
    if (isOpening) {
      //collapse all panel
      state = [false, false, false];
      state[id] = true;
    } else {
      state = [...accordionState];
      state[id] = false;
    }
    //check if no panel expanded
    if (state.every((value) => value === false)) {
      setCanExpandAll(true);
    }
    if (state.every((value) => value === true)) {
      setCanExpandAll(false);
    }

    setAccordionState(state);
  };

  const onExpandAllToggleClicked = () => {
    if (!canExpandAll) {
      setAccordionState([false, false, false]);
      setCanExpandAll(true);
    } else {
      setAccordionState([true, true, true]);
      setCanExpandAll(false);
    }
  };

  const onCarrierChanged = (carrier: ECarrier) => {
    setSelectedCarrier(carrier);
    const carrierAutoCalibrationResult = autoCalibrationResults.filter(
      (c) => c.carrier === carrier
    )[0];
    updateFineTuningResult(carrierAutoCalibrationResult);
    const carrierStorageCalibration = storageCalibrations.filter(
      (c) => c.carrier === carrier
    )[0];
    setCarrierStorageCalibration(carrierStorageCalibration);
    setVectorDeviation(vectorDeviations[ECarrier[carrier]]);
  };

  const onCarrierFineTuningUpdated = (
    carrierFineTuningResult: Ri360AutoCalibrationFineTuningResult[]
  ) => {
    setIsCalibrationUpdated(true);
    setCarrierFineTuningResult(carrierFineTuningResult);
  };

  //-------End Function-------//

  if (carrierAutoCalibrationResult == null) {
    return <div>Loading auto calibration</div>;
  }

  if (carrierStorageCalibration == null) {
    return <div>Loading storage calibration</div>;
  }

  if (carrierFineTuningResult.length == 0) {
    return <div>Loading auto calibration result</div>;
  }

  const getPitchClassName = (pitch: number) => {
    pitch = Math.abs(pitch);
    if (pitch < 0.01) {
      return "green";
    }

    if (pitch < 0.39) {
      return "orange";
    }

    return "red";
  };

  const getRollClassName = (roll: number) => {
    roll = Math.abs(roll);
    if (roll < 0.01) {
      return "green";
    }

    if (roll < 0.39) {
      return "orange";
    }

    return "red";
  };

  const getYawClassName = (yaw: number) => {
    yaw = Math.abs(yaw);
    if (yaw < 0.01) {
      return "green";
    }

    if (yaw < 0.08) {
      return "orange";
    }

    return "red";
  };

  const pitch = vectorDeviation?.x ?? 0;
  const roll = vectorDeviation?.y ?? 0;
  const yaw = vectorDeviation?.z ?? 0;

  return (
    <div
      style={{
        backgroundColor: "white",
        height: "100%",
        overflow: "auto",
      }}
    >
      {/*--------CARRIER SELECTOR--------*/}
      <div className="auto-calibration-command-panel-container">
        <div className="auto-calibration-command-panel-select">
          <label>Selected Carrier</label>
          <CommonDropDown<number>
            value={selectedCarrier}
            id="carrier-select"
            onChange={(value) => {
              onCarrierChanged(value as ECarrier);
            }}
            style={{
              width: 359,
              height: 32,
            }}
            dropDownDisplayValues={carrierDisplayValues}
            disabled={false}
          />
        </div>
        <div className="auto-calibration-command-panel-vector-deviation">
          <label>Vector Deviation</label>
          <label>Pitch(X)</label>
          <label className={getPitchClassName(pitch)}>
            {pitch.toFixed(2) + "°"}
          </label>
          <label>Roll(Y)</label>
          <label className={getRollClassName(roll)}>
            {roll.toFixed(2) + "°"}
          </label>
          <label>Yaw(Z)</label>
          <label className={getYawClassName(yaw)}>{yaw.toFixed(2) + "°"}</label>
          <div
            onMouseEnter={() => setShowStorageView(true)}
            onMouseLeave={() => setShowStorageView(false)}
          >
            <svg
              width="28"
              height="28"
              viewBox="0 0 28 28"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M14 21.5C18.1421 21.5 21.5 18.1421 21.5 14C21.5 9.85786 18.1421 6.5 14 6.5C9.85786 6.5 6.5 9.85786 6.5 14C6.5 18.1421 9.85786 21.5 14 21.5ZM15.5 10.25C15.5 11.0784 14.8284 11.75 14 11.75C13.1716 11.75 12.5 11.0784 12.5 10.25C12.5 9.42157 13.1716 8.75 14 8.75C14.8284 8.75 15.5 9.42157 15.5 10.25ZM15.5 13.25V18.5H12.5V13.25H15.5Z"
                fill="#0059CC"
              />
            </svg>
            {showStorageView && (
              <img alt="storage-view" src={String(storageView)} />
            )}
          </div>
        </div>
        <div className="auto-calibration-command-panel-buttons">
          <MtiButton
            variant="primary"
            colorVariant="alternate"
            onClick={() =>
              CalibrationApi.exportAutoCalibrationCamera(
                selectedCarrier,
                carrierStorageCalibration.footprint,
                carrierFineTuningResult
              )
            }
            disabled={psrState.state === EPsrState.Busy}
            style={{
              width: 147,
              letterSpacing: 0.45,
              height: 34,
            }}
          >
            Download
          </MtiButton>
          <MtiButton
            variant="primary"
            colorVariant="alternate"
            onClick={() => {
              setShowRunAutoCalibrationModal(true);
            }}
            disabled={psrState?.state === EPsrState.Busy}
            style={{
              width: 147,
              letterSpacing: 0.45,
              height: 34,
            }}
          >
            Auto Calibrate
          </MtiButton>
          <MtiButton
            variant="primary"
            colorVariant="alternate"
            onClick={() => {
              setShowUseManualCalModal(true);
            }}
            style={{
              width: 166,
              letterSpacing: 0.45,
              height: 34,
            }}
            disabled={carrierAutoCalibrationResult?.isOverriden ?? true}
          >
            Adjust Manually
          </MtiButton>
        </div>
      </div>

      {/*--------CALIBRATION RESULT--------*/}
      <AccordionToggle
        expanded={accordionState[CALIBRATION_RESULT]}
        onClick={() => onAccordionToggleClicked(CALIBRATION_RESULT)}
      >
        Calibration Result
      </AccordionToggle>
      <Collapse expanded={accordionState[CALIBRATION_RESULT]}>
        <Disabled disabled={false}>
          <div
            className="auto-calibration-command-panel-container"
            style={{ padding: 8 }}
          >
            <AutoCalibrationResultWithFineTuning
              psrState={psrState}
              psrIdentifier={psrIdentifier}
              storageCalibration={carrierStorageCalibration}
              carrierFineTuningResult={carrierFineTuningResult}
              setCarrierFineTuningResult={onCarrierFineTuningUpdated}
              disabled={carrierAutoCalibrationResult.isOverriden}
            />
          </div>
        </Disabled>
      </Collapse>
      {/*--------CALIBRATION TEST--------*/}
      <AccordionToggle
        expanded={accordionState[CALIBRATION_TEST]}
        onClick={() => onAccordionToggleClicked(CALIBRATION_TEST)}
      >
        Calibration Test
      </AccordionToggle>
      <Collapse expanded={accordionState[CALIBRATION_TEST]}>
        <Disabled disabled={false}>
          <div
            className="auto-calibration-command-panel-container"
            style={{ paddingTop: 15, paddingBottom: 15 }}
          >
            <AutoCalibrationTest
              autoCalibrationUpdated={isCalibrationUpdated}
              carrier={selectedCarrier}
              isPsrBusy={psrState.state === EPsrState.Busy}
              disabled={
                carrierAutoCalibrationResult.isOverriden &&
                carrierAutoCalibrationResult.updatedPositions === null
              }
              isUpdatedPositionAutoCalibrationResultExist={
                carrierAutoCalibrationResult.updatedPositions !== null
              }
              storageCalibration={carrierStorageCalibration}
              updatedCoordinateHotelConfigs={getCarrierAutoCalibrationFromCarrierFineTuning(
                carrierFineTuningResult
              )}
            />
          </div>
        </Disabled>
      </Collapse>
      <RunSingleAutoCalibrationModal
        show={showRunAutoCalibrationModal}
        onHide={() => {
          setShowRunAutoCalibrationModal(false);
        }}
        onRunAutoCalibration={() => {}}
        autoCalibrationResult={carrierAutoCalibrationResult}
        carrier={selectedCarrier}
      />
      <CalibrationProgressModal
        show={popupModalState === "INPROGRESS"}
        onHide={() => {}}
        stopCalibration={stopAutoCalibration}
        autoCalibratingState={
          autoCalibratingState?.modeState ?? ESuiteModeState.Starting
        }
      />
      <CalibrationSuccessModal
        show={popupModalState === "SUCCESS"}
        onHide={onHideDone}
      />
      <CalibrationFailModal
        show={popupModalState === "FAIL"}
        onHide={onHideDone}
        disableAnimation={disableDoneAnimation}
      />
      <UseManualCalibrationModal
        show={showUseManualCalModal}
        onHide={() => {
          setShowUseManualCalModal(false);
        }}
        doUseManualCalibration={useManualCalibration}
      />
    </div>
  );
}
